'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

type SettingsSection = 'profile' | 'notifications' | 'workspace' | 'security' | 'accessibility';

export default function SettingsContent() {
  const [section, setSection] = useState<SettingsSection>('profile');
  const [displayName, setDisplayName] = useState('Nur Aisyah');
  const [language, setLanguage] = useState('en');
  const [density, setDensity] = useState<'compact' | 'comfortable' | 'spacious'>('comfortable');
  const [defaultQueue, setDefaultQueue] = useState('MyGCC General');
  const [notifications, setNotifications] = useState({
    incomingCall: true,
    newMessage: true,
    caseUpdate: true,
    slaAlert: true,
    aiDraft: true,
    supervisorMessage: true,
    knowledgeUpdate: false,
    systemAlert: true,
  });

  const navItems: { key: SettingsSection; label: string; icon: string }[] = [
    { key: 'profile', label: 'Profile', icon: 'UserIcon' },
    { key: 'notifications', label: 'Notifications', icon: 'BellIcon' },
    { key: 'workspace', label: 'Workspace', icon: 'RectangleStackIcon' },
    { key: 'security', label: 'Security', icon: 'ShieldCheckIcon' },
    { key: 'accessibility', label: 'Accessibility', icon: 'AdjustmentsHorizontalIcon' },
  ];

  return (
    <div className="max-w-screen-xl mx-auto px-5 py-4 page-transition">
      <div className="mb-4">
        <h1 className="text-[18px] font-700 text-foreground">Settings</h1>
        <p className="text-[12px] text-muted-foreground mt-0.5">Manage your profile, preferences, and workspace settings</p>
      </div>

      <div className="flex gap-4">
        {/* Side nav */}
        <div className="w-48 flex-shrink-0">
          <div className="card-elevated p-2">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => setSection(item.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-600 transition-all mb-0.5 text-left ${section === item.key ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-2xl">
          {section === 'profile' && (
            <div className="card-elevated p-6 space-y-5">
              <h2 className="text-[14px] font-700 text-foreground border-b border-border pb-3">Profile Settings</h2>

              {/* Avatar */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-xl font-700">NA</div>
                <div>
                  <button onClick={() => toast.info('Upload photo')} className="btn-secondary px-3 py-1.5 text-[12px]">Change Photo</button>
                  <div className="text-[11px] text-muted-foreground mt-1">JPG, PNG up to 2MB</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-600 text-foreground mb-1.5">Display Name</label>
                  <input value={displayName} onChange={e => setDisplayName(e.target.value)} className="input-field" />
                </div>
                <div>
                  <label className="block text-[12px] font-600 text-foreground mb-1.5">Employee ID</label>
                  <input value="EMP-2024-0892" readOnly className="input-field bg-muted/50 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-[12px] font-600 text-foreground mb-1.5">Role</label>
                  <input value="Customer Service Officer" readOnly className="input-field bg-muted/50 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-[12px] font-600 text-foreground mb-1.5">Department</label>
                  <input value="MyGCC Operations" readOnly className="input-field bg-muted/50 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-[12px] font-600 text-foreground mb-1.5">Interface Language</label>
                  <select value={language} onChange={e => setLanguage(e.target.value)} className="select-field w-full">
                    <option value="en">English</option>
                    <option value="ms">Bahasa Malaysia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] font-600 text-foreground mb-1.5">Default Queue</label>
                  <select value={defaultQueue} onChange={e => setDefaultQueue(e.target.value)} className="select-field w-full">
                    {['MyGCC General', 'Immigration', 'Health Services', 'Transport', 'Taxation'].map(q => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-border">
                <button onClick={() => toast.success('Profile saved successfully')} className="btn-primary px-4 py-2 text-[13px]">Save Changes</button>
              </div>
            </div>
          )}

          {section === 'notifications' && (
            <div className="card-elevated p-6 space-y-4">
              <h2 className="text-[14px] font-700 text-foreground border-b border-border pb-3">Notification Preferences</h2>
              <div className="space-y-3">
                {Object.entries(notifications).map(([key, enabled]) => {
                  const labels: Record<string, { label: string; desc: string }> = {
                    incomingCall: { label: 'Incoming Call', desc: 'Alert when a new voice call is assigned' },
                    newMessage: { label: 'New Message', desc: 'Alert for new WhatsApp, chat, or email messages' },
                    caseUpdate: { label: 'Case Updates', desc: 'Notify when assigned cases are updated' },
                    slaAlert: { label: 'SLA Alerts', desc: 'Warn when cases approach SLA breach' },
                    aiDraft: { label: 'AI Draft Ready', desc: 'Notify when AI completes interaction summary' },
                    supervisorMessage: { label: 'Supervisor Messages', desc: 'Alerts from supervisors and team leads' },
                    knowledgeUpdate: { label: 'Knowledge Updates', desc: 'Notify when knowledge articles are updated' },
                    systemAlert: { label: 'System Alerts', desc: 'Critical system and maintenance notifications' },
                  };
                  const info = labels[key];
                  return (
                    <div key={key} className="flex items-center justify-between p-3 rounded-xl border border-border hover:bg-muted/20 transition-colors">
                      <div>
                        <div className="text-[12px] font-600 text-foreground">{info.label}</div>
                        <div className="text-[11px] text-muted-foreground">{info.desc}</div>
                      </div>
                      <button
                        onClick={() => setNotifications(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))}
                        className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-muted'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end pt-2 border-t border-border">
                <button onClick={() => toast.success('Notification preferences saved')} className="btn-primary px-4 py-2 text-[13px]">Save Preferences</button>
              </div>
            </div>
          )}

          {section === 'workspace' && (
            <div className="card-elevated p-6 space-y-5">
              <h2 className="text-[14px] font-700 text-foreground border-b border-border pb-3">Workspace Preferences</h2>

              <div>
                <label className="block text-[12px] font-700 text-foreground mb-2">Display Density</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['compact', 'comfortable', 'spacious'] as const).map(d => (
                    <button
                      key={d}
                      onClick={() => setDensity(d)}
                      className={`p-3 rounded-xl border-2 text-[12px] font-600 transition-all capitalize ${density === d ? 'border-primary bg-secondary text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Show AI Assist panel by default', key: 'aiPanel' },
                  { label: 'Auto-open Customer 360 on interaction accept', key: 'autoC360' },
                  { label: 'Show sentiment indicators in interaction list', key: 'sentimentList' },
                  { label: 'Enable keyboard shortcuts', key: 'shortcuts' },
                  { label: 'Show SLA countdown timers', key: 'slaTimer' },
                ].map(pref => (
                  <div key={pref.key} className="flex items-center justify-between p-3 rounded-xl border border-border">
                    <span className="text-[12px] font-500 text-foreground">{pref.label}</span>
                    <button className="relative w-10 h-5 rounded-full bg-primary">
                      <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end pt-2 border-t border-border">
                <button onClick={() => toast.success('Workspace preferences saved')} className="btn-primary px-4 py-2 text-[13px]">Save Preferences</button>
              </div>
            </div>
          )}

          {section === 'security' && (
            <div className="card-elevated p-6 space-y-5">
              <h2 className="text-[14px] font-700 text-foreground border-b border-border pb-3">Security Settings</h2>

              <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                <Icon name="ShieldCheckIcon" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-[12px] font-700 text-green-700">MyDigital ID Authentication Active</div>
                  <div className="text-[11px] text-green-600">Your account is secured with MyDigital ID biometric authentication.</div>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Active Sessions', value: '1 session (this device)', action: 'Manage' },
                  { label: 'Last Login', value: '16 Aug 2026, 08:00 MYT', action: null },
                  { label: 'Login Method', value: 'MyDigital ID SSO', action: null },
                  { label: 'Session Timeout', value: '30 minutes of inactivity', action: 'Change' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between p-3 rounded-xl border border-border">
                    <div>
                      <div className="text-[12px] font-600 text-foreground">{item.label}</div>
                      <div className="text-[11px] text-muted-foreground">{item.value}</div>
                    </div>
                    {item.action && (
                      <button onClick={() => toast.info(item.action!)} className="btn-ghost px-2.5 py-1 text-[11px] border border-border">{item.action}</button>
                    )}
                  </div>
                ))}
              </div>

              <div className="pt-2 border-t border-border">
                <button onClick={() => toast.info('Signing out...')} className="btn-danger px-4 py-2 text-[13px] flex items-center gap-2">
                  <Icon name="ArrowRightOnRectangleIcon" size={14} />
                  Sign Out of All Sessions
                </button>
              </div>
            </div>
          )}

          {section === 'accessibility' && (
            <div className="card-elevated p-6 space-y-5">
              <h2 className="text-[14px] font-700 text-foreground border-b border-border pb-3">Accessibility</h2>
              <div className="space-y-3">
                {[
                  { label: 'High Contrast Mode', desc: 'Increase contrast for better visibility' },
                  { label: 'Reduce Motion', desc: 'Minimise animations and transitions' },
                  { label: 'Large Text Mode', desc: 'Increase base font size by 20%' },
                  { label: 'Screen Reader Optimisation', desc: 'Enhanced ARIA labels and focus management' },
                  { label: 'Keyboard Navigation', desc: 'Full keyboard navigation support' },
                ].map((pref, i) => (
                  <div key={pref.label} className="flex items-center justify-between p-3 rounded-xl border border-border">
                    <div>
                      <div className="text-[12px] font-600 text-foreground">{pref.label}</div>
                      <div className="text-[11px] text-muted-foreground">{pref.desc}</div>
                    </div>
                    <button className={`relative w-10 h-5 rounded-full transition-colors ${i === 4 ? 'bg-primary' : 'bg-muted'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${i === 4 ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
